import React from 'react';
import Dialog from '@mui/material/Dialog';
import '../../css/main.css';
import API from '../../api/api';
import LinearProgress from '@mui/material/LinearProgress';
import { Component } from 'react';
import PropTypes from 'prop-types';
import SecuredZone from '../../components/SecuredZone';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import EmployeeDialog from '../../components/dialogs/employee-dialog/employee-dialog';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Chip from '@mui/material/Chip';
import { handleApiError } from '../../utils/errorHandler';
import TablePagination from '@mui/material/TablePagination';

const FireApi = new API();

class Employee extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      dialogEmployee: false,
      editUser: null,
      users: [],
      groups: [],
      currentUserId: null,
      page: 0,
      rowsPerPage: 10,
      totalCount: 0,
    };
  }

  componentDidMount() {
    this.getUsers();
    this.getGroups();
    this.getCurrentUser();
  }

  getCurrentUser() {
    FireApi.endpoints.me.getAll()
      .then((userGet) => {
        let userData = null;
        if (userGet.data && userGet.data.user) {
          userData = userGet.data.user;
        } else if (userGet.data && Array.isArray(userGet.data) && userGet.data.length > 0) {
          userData = userGet.data[0];
        } else if (userGet.data && userGet.data.id) {
          userData = userGet.data;
        }
        
        if (userData && userData.id) {
          this.setState({ currentUserId: userData.id });
        }
      })
      .catch(() => {
        // Fehler beim Laden des aktuellen Benutzers ignorieren
      });
  }

  getUsers(page = null, rowsPerPage = null) {
    const currentPage = page !== null ? page : this.state.page;
    const currentRowsPerPage = rowsPerPage !== null ? rowsPerPage : this.state.rowsPerPage;
    
    FireApi.endpoints.user.getAll(
      { withCredentials: true, crossDomain: true },
      { page: currentPage + 1, page_size: currentRowsPerPage }
    )
      .then((usersGet) => {
        if (usersGet && usersGet.data) {
          if (usersGet.data.results && Array.isArray(usersGet.data.results)) {
            this.setState({ 
              users: usersGet.data.results, 
              totalCount: usersGet.data.count || usersGet.data.results.length,
              loading: false 
            });
          } else if (Array.isArray(usersGet.data)) {
            this.setState({ 
              users: usersGet.data, 
              totalCount: usersGet.data.length,
              loading: false 
            });
          } else {
            this.setState({ users: [], totalCount: 0, loading: false });
          }
        } else {
          this.setState({ users: [], totalCount: 0, loading: false });
        }
      })
      .catch((error) => {
        handleApiError(error, 'beim Laden der Benutzer', () => {
          this.setState({ users: [], totalCount: 0, loading: false });
        });
      });
  }

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage }, () => {
      this.getUsers(newPage, null);
    });
  };

  handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    this.setState({ rowsPerPage: newRowsPerPage, page: 0 }, () => {
      this.getUsers(0, newRowsPerPage);
    });
  };

  getGroups() {
    FireApi.endpoints.groups.getAll()
      .then((groupsGet) => {
        if (groupsGet.data && groupsGet.data.results) {
          this.setState({ groups: groupsGet.data.results });
        } else if (Array.isArray(groupsGet.data)) {
          this.setState({ groups: groupsGet.data });
        }
      })
      .catch(() => {
        // Groups endpoint might not exist, continue without groups
      });
  }

  closeEmployee() {
    this.setState({ dialogEmployee: false, editUser: null });
  }

  openEmployee() {
    this.setState({ dialogEmployee: true, editUser: null });
  }

  editEmployee(user) {
    this.setState({ dialogEmployee: true, editUser: user });
  }

  deleteEmployee(userId) {
    if (window.confirm('Möchten Sie diesen Benutzer wirklich löschen?')) {
      this.setState({ loading: true });
      FireApi.endpoints.user.delete({ id: userId })
        .then(() => {
          this.setState({ loading: false });
          this.getUsers();
        })
        .catch((error) => {
          handleApiError(error, 'beim Löschen des Benutzers', () => {
            this.setState({ loading: false });
          });
        });
    }
  }

  submitEmployee(data) {
    this.closeEmployee();
    this.setState({ loading: true });

    if (data.isAddMode) {
      // Create new user - POST /v2/user/
      // email, password, optional username, and groups object
      const userData = {
        email: data.user.email,
        password: data.user.password,
      };
      
      // Add username only if provided
      if (data.user.username) {
        userData.username = data.user.username;
      }

      // Add groups if provided (format: { "groupName": { "read": 1, "write": 1, "delete": 0 } })
      if (data.user.groups && Object.keys(data.user.groups).length > 0) {
        // Filter out groups with all permissions set to 0
        const filteredGroups = {};
        Object.keys(data.user.groups).forEach(groupName => {
          const perms = data.user.groups[groupName];
          if (perms.read === 1 || perms.write === 1 || perms.delete === 1) {
            filteredGroups[groupName] = perms;
          }
        });
        if (Object.keys(filteredGroups).length > 0) {
          userData.groups = filteredGroups;
        }
      }

      FireApi.endpoints.user.create(userData)
        .then(() => {
          this.setState({ loading: false });
          this.getUsers();
        })
        .catch((error) => {
          handleApiError(error, 'beim Erstellen des Benutzers', () => {
            this.setState({ loading: false });
          });
        });
    } else {
      // Update existing user - PUT /v2/user/{id}
      // email, optional username, and groups object
      const userData = {
        id: data.user.id,
        email: data.user.email,
      };
      
      // Add username only if provided
      if (data.user.username) {
        userData.username = data.user.username;
      }

      // Prüfe ob es der eigene Benutzer ist - dann keine Gruppen-Berechtigungen ändern
      const isOwnUser = this.state.currentUserId && data.user.id && this.state.currentUserId === data.user.id;
      
      // Add groups if provided (only send groups that have at least one permission)
      // NICHT wenn es der eigene Benutzer ist
      if (!isOwnUser && data.user.groups && Object.keys(data.user.groups).length > 0) {
        // Filter out groups with all permissions set to 0
        const filteredGroups = {};
        Object.keys(data.user.groups).forEach(groupName => {
          const perms = data.user.groups[groupName];
          if (perms.read === 1 || perms.write === 1 || perms.delete === 1) {
            filteredGroups[groupName] = perms;
          }
        });
        if (Object.keys(filteredGroups).length > 0) {
          userData.groups = filteredGroups;
        }
      }

      FireApi.endpoints.user.update(userData)
        .then(() => {
          this.setState({ loading: false });
          this.getUsers();
        })
        .catch((error) => {
          handleApiError(error, 'beim Aktualisieren des Benutzers', () => {
            this.setState({ loading: false });
          });
        });
    }
  }

  loadGroups() {
    return new Promise((resolve) => {
      if (this.state.groups.length > 0) {
        resolve(this.state.groups);
      } else {
        FireApi.endpoints.groups.getAll()
          .then((groupsGet) => {
            let groups = [];
            if (groupsGet.data && groupsGet.data.results) {
              groups = groupsGet.data.results;
            } else if (Array.isArray(groupsGet.data)) {
              groups = groupsGet.data;
            }
            this.setState({ groups });
            resolve(groups);
          })
          .catch(() => {
            resolve([]);
          });
      }
    });
  }

  render() {
    return (
      <SecuredZone 
        title="Mitarbeiter"
        breadcrumbs={{ '/': 'Dashboard', '/employees': 'Mitarbeiter' }}
        loading={this.state.loading ? <LinearProgress color="primary" /> : <span className="loader-span"></span>}
        menu={{ 'Benutzer Hinzufügen': this.openEmployee.bind(this) }}
      >
        <div className="overflow-visibile">
        <TableContainer style={{overflow: 'visibile'}} className="settings-table" component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="employees table">
            <TableHead>
              <TableRow>
                <TableCell>E-Mail</TableCell>
                <TableCell align="left">Benutzername</TableCell>
                <TableCell align="left">Verifiziert</TableCell>
                <TableCell align="left" size="small">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(this.state.users) ? (
                <>
                  {this.state.users.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell component="th" scope="row">
                        {row.email}
                      </TableCell>
                      <TableCell align="left">
                        {row.username || '-'}
                      </TableCell>
                      <TableCell align="left">
                        {row.verified === 1 ? (
                          <Chip label="Ja" color="success" size="small" />
                        ) : (
                          <Chip label="Nein" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell size="small">
                        <div className="table-buttons">
                          <IconButton 
                            onClick={() => { this.deleteEmployee(row.id); }} 
                            size="small"
                            color="error"
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                          <span />
                          <IconButton 
                            onClick={() => { this.editEmployee(row); }} 
                            size="small"
                          >
                            <EditIcon fontSize="inherit" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Keine Benutzer gefunden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={this.state.totalCount}
          page={this.state.page}
          onPageChange={this.handleChangePage}
          rowsPerPage={this.state.rowsPerPage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={({ from, to, count, page }) => {
            const rowsPerPage = this.state.rowsPerPage;
            const totalPages = count > 0 ? Math.ceil(count / rowsPerPage) : 1;
            const currentPage = page + 1;
            return `Seite ${currentPage} von ${totalPages}`;
          }}
          className="table-pagination"
        />

        <Dialog 
          open={this.state.dialogEmployee} 
          onClose={this.closeEmployee.bind(this)}
          maxWidth="md"
          fullWidth
        >
          <EmployeeDialog
            userData={this.state.editUser}
            close={this.closeEmployee.bind(this)}
            submit={this.submitEmployee.bind(this)}
            loadGroups={this.loadGroups.bind(this)}
            currentUserId={this.state.currentUserId}
          />
        </Dialog>
        </div>
      </SecuredZone>
    );
  }
}

Employee.propTypes = {
  history: PropTypes.object
};

export default Employee;
